import { Injectable, ComponentRef, ViewContainerRef, ApplicationRef, Injector, InjectionToken, EnvironmentInjector, createComponent } from '@angular/core';
import { SnackBarComponent } from './snack-bar.component';

export const SNACK_BAR_DATA = new InjectionToken<any>('SnackBarData');

@Injectable({
  providedIn: 'root'
})
export class CustomSnackBarService {
  private snackBarRef: ComponentRef<SnackBarComponent> | null = null;

  constructor(
    private environmentInjector: EnvironmentInjector,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  open(data: any, config: { duration?: number } = {}) {
    // Remove existing snackbar if any
    this.dismiss();

    // Normalize data to consistent format
    const normalizedData = this.normalizeData(data);

    // ComponentFactoryResolver was removed in Angular 22 - shim over the standalone createComponent API
    const componentFactory = {
      create: (injector: Injector) =>
        createComponent(SnackBarComponent, {
          environmentInjector: this.environmentInjector,
          elementInjector: injector,
        }),
    };
    
    // Create component normally
    this.snackBarRef = componentFactory.create(this.injector);
    
    // Set normalized data directly on the component instance AFTER creation but BEFORE attaching to DOM
    this.snackBarRef.instance.data = normalizedData;
    console.log('Service setting normalized data:', normalizedData);
    
    // Force change detection
    this.snackBarRef.changeDetectorRef.detectChanges();
    
    // Set snackBarRef with proper binding
    const serviceRef = this;
    this.snackBarRef.instance.snackBarRef = {
      dismiss: () => serviceRef.dismiss()
    };

    // Attach to application
    this.appRef.attachView(this.snackBarRef.hostView);

    // Get DOM element
    const domElem = (this.snackBarRef.hostView as any).rootNodes[0] as HTMLElement;
    domElem.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      background: #323232;
      color: white;
      padding: 14px 16px;
      border-radius: 4px;
      box-shadow: 0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12);
      width: 400px;
      max-width: 400px;
      word-wrap: break-word;
      white-space: pre-wrap;
      animation: slideIn 0.3s ease-out;
    `;

    // Add animation keyframes if not already added
    if (!document.getElementById('snackbar-styles')) {
      const style = document.createElement('style');
      style.id = 'snackbar-styles';
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateX(-50%) translateY(100%); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        @keyframes slideOut {
          from { transform: translateX(-50%) translateY(0); opacity: 1; }
          to { transform: translateX(-50%) translateY(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(domElem);

    // Auto dismiss after duration
    if (config.duration) {
      setTimeout(() => {
        this.dismiss();
      }, config.duration);
    }

    return {
      dismiss: () => this.dismiss()
    };
  }

  dismiss() {
    if (this.snackBarRef) {
      const domElem = (this.snackBarRef.hostView as any).rootNodes[0] as HTMLElement;
      
      // Add slide out animation
      domElem.style.animation = 'slideOut 0.3s ease-in';
      
      setTimeout(() => {
        this.appRef.detachView(this.snackBarRef!.hostView);
        this.snackBarRef!.destroy();
        this.snackBarRef = null;
      }, 300);
    }
  }

  private normalizeData(data: any): { message: string, orderId: string } {
    console.log('normalizeData input:', data);
    let message = '';
    let orderId = '';

    // Extract message
    if (typeof data === 'string') {
      message = data;
    } else if (data?.message) {
      if (typeof data.message === 'string') {
        message = data.message;
      } else if (data.message.statusMessage) {
        message = data.message.statusMessage;
      }
    } else if (data?.data?.message) {
      message = data.data.message;
    }

    // Extract orderId - check multiple possible locations
    if (data?.orderId) {
      orderId = data.orderId;
    } else if (data?.data?.businessObjectId) {
      orderId = data.data.businessObjectId;
    } else if (data?.data?.businessObjectNumber) {
      orderId = data.data.businessObjectNumber;
    } else if (data?.data?.businessObjectIds && Array.isArray(data.data.businessObjectIds) && data.data.businessObjectIds.length > 0) {
      orderId = data.data.businessObjectIds[0].businessObjectId || data.data.businessObjectIds[0].businessObjectNumber || '';
    }

    // If orderId is still empty but message contains an ID pattern, extract it
    if (!orderId && message) {
      const idPattern = /([A-Z]{3}\d{8})/;
      const match = message.match(idPattern);
      if (match) {
        orderId = match[1];
      }
    }

    console.log('normalizeData output - message:', message, 'orderId:', orderId);
    return { message, orderId };
  }
}